/**
 * Something is broken, and who has it now.
 *
 * A complaint is the one thing in this system a resident and the committee both
 * write to, so the order it moves in matters more than usual: a resident
 * confirming a repair nobody made, or a committee closing one the resident
 * never saw, are both ways of losing the argument that follows.
 *
 * The rules enforce who may move it. This says what a move even is, and both
 * sides read it, so the screen never offers a step the rules will refuse.
 */

export const COMPLAINT_STATUSES = [
  "reported",
  "assigned",
  "attended",
  "confirmed",
  "closed",
] as const;
export type ComplaintStatus = (typeof COMPLAINT_STATUSES)[number];

export const COMPLAINT_CATEGORIES = [
  "plumbing",
  "electrical",
  "lift",
  "cleaning",
  "security",
  "other",
] as const;
export type ComplaintCategory = (typeof COMPLAINT_CATEGORIES)[number];

export type Mover = "resident" | "committee";

export class ComplaintError extends Error {}

/**
 * Who may make which move.
 *
 * Confirming is the resident's alone, because it is the only step that says the
 * work was actually done, and the committee marking its own work confirmed
 * would make the word mean nothing. So is saying it is still not fixed: the
 * repair goes back to reported, in the committee's hands again, rather than
 * sitting at "said to be fixed" looking finished. Everything else is the
 * committee's.
 */
const MOVES: Record<ComplaintStatus, Partial<Record<ComplaintStatus, Mover>>> = {
  reported: { assigned: "committee", closed: "committee" },
  assigned: { attended: "committee", closed: "committee" },
  attended: {
    reported: "resident",
    confirmed: "resident",
    assigned: "committee",
    closed: "committee",
  },
  confirmed: { closed: "committee" },
  closed: {},
};

/**
 * Whether this mover may make this move.
 *
 * `from` is read off a document, so it can be a word this build has never
 * heard of: an older app against newer data, or a row written by something
 * that invented a status. Such a status has no legal moves, which is the
 * honest answer and stops one unrecognised row from taking the screen down
 * with it.
 */
export function canMove(from: ComplaintStatus, to: ComplaintStatus, mover: Mover): boolean {
  return MOVES[from]?.[to] === mover;
}

/** The steps a mover can take from here, in the order a screen should offer them. */
export function movesFrom(from: ComplaintStatus, mover: Mover): ComplaintStatus[] {
  return COMPLAINT_STATUSES.filter((to) => canMove(from, to, mover));
}

export interface TimelineEntry {
  at: string;
  status: ComplaintStatus;
  by: string;
  note?: string;
}

export interface Assignee {
  name: string;
  phone: string;
  trade: string;
}

/**
 * The next timeline entry, checked before it is written.
 *
 * Saying someone is on it is one tap: the committee knows its own workers, and
 * a form asking for a name, a trade and a phone number every time is work the
 * committee was not doing. A name given anyway is kept and shown.
 */
export function nextEntry(options: {
  from: ComplaintStatus;
  to: ComplaintStatus;
  mover: Mover;
  by: string;
  at: Date;
  note?: string;
  assignee?: Assignee;
}): TimelineEntry {
  const { from, to, mover, by, at, note, assignee } = options;

  if (!canMove(from, to, mover)) {
    throw new ComplaintError(`A ${mover} cannot move a complaint from ${from} to ${to}`);
  }
  if (assignee !== undefined && !assignee.name.trim()) {
    throw new ComplaintError("A worker named on a complaint needs a name");
  }

  return {
    at: at.toISOString(),
    status: to,
    by,
    ...(note?.trim() ? { note: note.trim() } : {}),
  };
}

/** Waiting on the committee, waiting on the resident, or done. */
export function waitingOn(status: ComplaintStatus): Mover | null {
  if (status === "closed") {
    return null;
  }
  return status === "attended" ? "resident" : "committee";
}

/** A repair report as both apps read it. */
export interface ComplaintDoc {
  id: string;
  unitId: string;
  category: ComplaintCategory;
  description: string;
  status: ComplaintStatus;
  assignee: Assignee | null;
  timeline: TimelineEntry[];
  reportedAt: Date | null;
}

function dateOf(value: unknown): Date | null {
  if (value instanceof Date) {
    return value;
  }
  if (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  if (typeof value === "string" && !Number.isNaN(Date.parse(value))) {
    return new Date(value);
  }
  return null;
}

/**
 * Lenient about everything but the unit: a status this build has never heard of
 * is kept (and then has no legal moves), rather than taking a list down.
 */
export function toComplaint(id: string, data: Record<string, unknown>): ComplaintDoc {
  const { unitId, category, description, status, assignee, timeline, reportedAt } = data;
  if (typeof unitId !== "string" || unitId === "") {
    throw new ComplaintError(`Complaint ${id} names no unit`);
  }
  return {
    id,
    unitId,
    category: (typeof category === "string" ? category : "other") as ComplaintCategory,
    description: typeof description === "string" ? description : "",
    status: (typeof status === "string" ? status : "reported") as ComplaintStatus,
    assignee: assignee && typeof assignee === "object" ? (assignee as Assignee) : null,
    timeline: Array.isArray(timeline) ? (timeline as TimelineEntry[]) : [],
    reportedAt: dateOf(reportedAt),
  };
}
